// src/components/users/UserManagement.js
import React, { useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Card, Table, Badge, Button, Modal, FormField, Input, Select, SearchInput, ConfirmDialog, ResponsiveGrid } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';
import { ROLES, ROLE_LABELS, ROLE_COLORS, MODULE_LIST, DEFAULT_MODULES } from '../../data/store';

export default function UserManagement() {
  const { users, addUser, updateUser, deleteUser, clients } = useApp();
  const { isMobile } = useResponsive();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: ROLES.ENCODER, phone: '', modules: [], clientId: '' });

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    ROLE_LABELS[u.role]?.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setEditUser(null);
    setForm({ name: '', email: '', password: '', role: ROLES.ENCODER, phone: '', modules: DEFAULT_MODULES.encoder, clientId: '' });
    setModalOpen(true);
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({ name: user.name, email: user.email, password: user.password, role: user.role, phone: user.phone || '', modules: user.modules || [], clientId: user.clientId || '' });
    setModalOpen(true);
  };

  const handleRoleChange = (role) => {
    setForm(f => ({ ...f, role, modules: DEFAULT_MODULES[role] || [] }));
  };

  const toggleModule = (moduleId) => {
    setForm(f => ({
      ...f,
      modules: f.modules.includes(moduleId)
        ? f.modules.filter(m => m !== moduleId)
        : [...f.modules, moduleId]
    }));
  };

  const handleSave = () => {
    if (!form.name || !form.email) return;
    if (editUser) {
      updateUser(editUser.id, form);
    } else {
      addUser(form);
    }
    setModalOpen(false);
  };

  const availableModules = MODULE_LIST.filter(m => m.roles.includes(form.role));

  const columns = [
    {
      key: 'name', label: 'User',
      render: (val, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: (ROLE_COLORS[row.role] || '#f97316') + '20', color: ROLE_COLORS[row.role] || '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, fontFamily: 'Syne, sans-serif' }}>
            {row.avatar}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--textPrimary)' }}>{val}</div>
            <div style={{ fontSize: 11, color: 'var(--textFaint)' }}>{row.email}</div>
          </div>
        </div>
      )
    },
    { key: 'phone', label: 'Phone', render: v => <span style={{ color: 'var(--textMuted)', fontSize: 12 }}>{v || '—'}</span> },
    {
      key: 'role', label: 'Role',
      render: (v) => <Badge color={ROLE_COLORS[v]}>{ROLE_LABELS[v]}</Badge>
    },
    {
      key: 'modules', label: 'Modules',
      render: (v) => <span style={{ fontSize: 12, color: 'var(--textMuted)' }}>{v?.length || 0} modules</span>
    },
    {
      key: 'status', label: 'Status',
      render: v => <Badge color={v === 'active' ? '#10b981' : '#6b7280'}>{v}</Badge>
    },
    { key: 'createdAt', label: 'Created', render: v => <span style={{ fontSize: 12, color: 'var(--textFaint)' }}>{v}</span> },
    {
      key: 'id', label: 'Actions',
      render: (id, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); setConfirmDelete(row); }}>Del</Button>
        </div>
      )
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, alignItems: isMobile ? 'stretch' : 'center', marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search users by name, email or role..." />
          </div>
          <Button onClick={openAdd} icon="＋">Add User</Button>
        </div>

        <ResponsiveGrid columns={4} tabletColumns={2} mobileColumns={2} gap={12} style={{ marginBottom: 20 }}>
          {Object.entries(ROLES).map(([, role]) => {
            const count = users.filter(u => u.role === role).length;
            return (
              <div key={role} style={{ background: 'var(--bgInput)', border: `1px solid ${ROLE_COLORS[role]}22`, borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: ROLE_COLORS[role], textTransform: 'uppercase', letterSpacing: '0.8px' }}>{ROLE_LABELS[role]}</div>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 26, color: 'var(--textPrimary)', marginTop: 4 }}>{count}</div>
              </div>
            );
          })}
        </ResponsiveGrid>

        <Table columns={columns} data={filtered} emptyMessage="No users found" />
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editUser ? 'Edit User' : 'Add New User'} width={600}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
          <FormField label="Full Name" required>
            <Input value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="Enter full name" required />
          </FormField>
          <FormField label="Phone">
            <Input value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} placeholder="+63 9XX XXX XXXX" />
          </FormField>
          <FormField label="Email Address" required>
            <Input value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} placeholder="user@email.com" type="email" required />
          </FormField>
          <FormField label="Password" required>
            <Input value={form.password} onChange={v => setForm(f => ({ ...f, password: v }))} placeholder="Password" type="password" required />
          </FormField>
          <FormField label="Role" required>
            <Select value={form.role} onChange={handleRoleChange} options={Object.entries(ROLES).map(([, v]) => ({ value: v, label: ROLE_LABELS[v] }))} />
          </FormField>
          {form.role === ROLES.CLIENT && (
            <FormField label="Linked Client">
              <Select value={form.clientId} onChange={v => setForm(f => ({ ...f, clientId: v }))} placeholder="Select client" options={clients.map(c => ({ value: c.id, label: c.name }))} />
            </FormField>
          )}
        </div>

        {form.role !== ROLES.CLIENT && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Assigned Modules</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 8 }}>
              {availableModules.map(mod => (
                <label key={mod.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: form.modules.includes(mod.id) ? 'rgba(249,115,22,0.08)' : 'var(--bgInput)', border: `1px solid ${form.modules.includes(mod.id) ? 'rgba(249,115,22,0.3)' : '#1e2a4a'}`, borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s' }}>
                  <input type="checkbox" checked={form.modules.includes(mod.id)} onChange={() => toggleModule(mod.id)} style={{ display: 'none' }} />
                  <div style={{ width: 16, height: 16, borderRadius: 4, background: form.modules.includes(mod.id) ? '#f97316' : 'transparent', border: `2px solid ${form.modules.includes(mod.id) ? '#f97316' : '#2a3a5a'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {form.modules.includes(mod.id) && <span style={{ color: '#fff', fontSize: 9, fontWeight: 800 }}>✓</span>}
                  </div>
                  <span style={{ fontSize: 12, color: form.modules.includes(mod.id) ? 'var(--textPrimary)' : 'var(--textMuted)', fontWeight: form.modules.includes(mod.id) ? 600 : 400 }}>{mod.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSave}>{editUser ? 'Save Changes' : 'Create User'}</Button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => deleteUser(confirmDelete.id)}
        title="Delete User"
        message={`Are you sure you want to delete "${confirmDelete?.name}"? This action cannot be undone.`}
      />
    </div>
  );
}
